import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Mail, 
  Bell, 
  Bot, 
  GitBranch, 
  Clock, 
  Webhook,
  Plus,
  Trash2,
  Save,
  Play,
  Database,
  Zap,
  Link as LinkIcon,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

interface WorkflowNode {
  id: string;
  type: string;
  name: string;
  config: any;
  position?: { x: number; y: number };
}

export function AutomationEditor() {
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [trigger, setTrigger] = useState('manual');
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const nodeTypes = [
    { 
      type: 'email', 
      label: 'Enviar Email', 
      icon: Mail, 
      color: 'from-blue-500 to-cyan-500',
      description: 'Envía correos corporativos con plantillas'
    },
    { 
      type: 'notification', 
      label: 'Notificación', 
      icon: Bell, 
      color: 'from-green-500 to-emerald-500',
      description: 'Envía notificaciones push internas'
    },
    { 
      type: 'ai_agent', 
      label: 'Agente IA', 
      icon: Bot, 
      color: 'from-purple-500 to-pink-500',
      description: 'Procesa con Claude o ChatGPT'
    },
    { 
      type: 'condition', 
      label: 'Condición', 
      icon: GitBranch, 
      color: 'from-orange-500 to-red-500',
      description: 'Lógica condicional (if/else)'
    },
    { 
      type: 'webhook', 
      label: 'Webhook', 
      icon: Webhook, 
      color: 'from-yellow-500 to-orange-500',
      description: 'Llama a APIs externas'
    },
    { 
      type: 'database', 
      label: 'Base de Datos', 
      icon: Database, 
      color: 'from-indigo-500 to-blue-500',
      description: 'Lee o escribe en BD'
    },
  ];

  const addNode = (type: string) => {
    const nodeType = nodeTypes.find(n => n.type === type);
    const newNode: WorkflowNode = {
      id: `node_${Date.now()}`,
      type,
      name: `${nodeType?.label} ${nodes.length + 1}`,
      config: {},
    };
    setNodes([...nodes, newNode]);
    toast.success(`Nodo "${nodeType?.label}" agregado`);
  };

  const removeNode = (id: string) => {
    setNodes(nodes.filter(n => n.id !== id));
    toast.info('Nodo eliminado');
  };

  const generateFromPrompt = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Escribe una descripción del flujo');
      return;
    }

    setIsGenerating(true);
    toast.info('Generando flujo con IA...');

    // Simular generación con IA
    setTimeout(() => {
      // Ejemplo de flujo generado
      const generatedNodes: WorkflowNode[] = [
        {
          id: 'node_1',
          type: 'condition',
          name: 'Verificar condición',
          config: { condition: aiPrompt }
        },
        {
          id: 'node_2',
          type: 'ai_agent',
          name: 'Procesar con IA',
          config: { prompt: aiPrompt }
        },
        {
          id: 'node_3',
          type: 'email',
          name: 'Enviar resultado',
          config: { template: 'resultado' }
        }
      ];

      setNodes(generatedNodes);
      setWorkflowName(`Flujo: ${aiPrompt.substring(0, 50)}`);
      setIsGenerating(false);
      toast.success('¡Flujo generado! Revisa y ajusta los nodos');
    }, 2000);
  };

  const saveWorkflow = () => {
    const workflow = {
      name: workflowName,
      description: workflowDescription,
      trigger,
      nodes,
      status: 'DRAFT',
    };
    console.log('Saving workflow:', workflow);
    toast.success('Automatización guardada correctamente');
  };

  const testWorkflow = () => {
    toast.info('Ejecutando prueba del flujo...');
    setTimeout(() => {
      toast.success('Flujo ejecutado correctamente ✓');
    }, 1500);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl mb-2">Editor de Automatizaciones</h1>
          <p className="text-muted-foreground">
            Crea flujos visuales con nuestro editor drag & drop
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={testWorkflow}>
            <Play className="mr-2 h-4 w-4" />
            Probar
          </Button>
          <Button onClick={saveWorkflow}>
            <Save className="mr-2 h-4 w-4" />
            Guardar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="visual" className="w-full">
        <TabsList>
          <TabsTrigger value="visual">Editor Visual</TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="mr-2 h-4 w-4" />
            Generar con IA
          </TabsTrigger>
          <TabsTrigger value="config">Configuración</TabsTrigger>
        </TabsList>

        {/* AI Generation Tab */}
        <TabsContent value="ai" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Generación Inteligente de Flujos</CardTitle>
              <CardDescription>
                Describe qué quieres automatizar y nuestra IA generará el flujo inicial
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="prompt">Describe tu automatización</Label>
                <Textarea
                  id="prompt"
                  placeholder="Ejemplo: Quiero que cada lunes a las 9am se envíe un correo con el reporte semanal a todos los managers, y si hay algún problema crítico, que se notifique al equipo de soporte"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={5}
                  disabled={isGenerating}
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={generateFromPrompt} 
                  disabled={isGenerating}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generar Flujo
                    </>
                  )}
                </Button>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm mb-2">💡 Ejemplos de prompts efectivos:</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Notificar al equipo cuando un activo requiera certificación</li>
                  <li>Enviar ofertas personalizadas basadas en el perfil del cliente</li>
                  <li>Procesar feedback de clientes y clasificarlo por sentimiento</li>
                  <li>Generar reportes automáticos cada viernes</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visual Editor Tab */}
        <TabsContent value="visual" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              {/* Canvas Area */}
              <Card>
                <CardHeader>
                  <CardTitle>Flujo de Trabajo</CardTitle>
                  <CardDescription>
                    Arrastra y suelta nodos para construir tu automatización
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Trigger Node */}
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-primary/10 to-purple-500/10 rounded-lg border-2 border-dashed border-primary/30">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-purple-600">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm">Trigger: {trigger === 'manual' ? 'Manual' : trigger === 'schedule' ? 'Programado' : 'Webhook'}</p>
                      <p className="text-xs text-muted-foreground">Inicio del flujo</p>
                    </div>
                  </div>

                  {/* Flow Nodes */}
                  {nodes.length > 0 && (
                    <div className="flex flex-col items-center gap-2">
                      <ArrowRight className="h-5 w-5 text-muted-foreground rotate-90" />
                    </div>
                  )}

                  {nodes.map((node, index) => {
                    const nodeType = nodeTypes.find(t => t.type === node.type);
                    if (!nodeType) return null;
                    const Icon = nodeType.icon;

                    return (
                      <React.Fragment key={node.id}>
                        <div className="flex items-center gap-3 p-4 border-2 rounded-lg hover:border-primary/50 transition-colors group">
                          <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${nodeType.color}`}>
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">{nodeType.label}</p>
                            <p className="text-xs text-muted-foreground">{node.name}</p>
                          </div>
                          <Badge variant="outline">Paso {index + 1}</Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeNode(node.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>

                        {index < nodes.length - 1 && (
                          <div className="flex flex-col items-center gap-2">
                            <ArrowRight className="h-5 w-5 text-muted-foreground rotate-90" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {nodes.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
                      <Zap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="mb-2">No hay nodos agregados</p>
                      <p className="text-sm">Agrega nodos desde el panel lateral o genera un flujo con IA</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Node Palette */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>Nodos Disponibles</CardTitle>
                  <CardDescription>
                    Arrastra para agregar al flujo
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {nodeTypes.map((nodeType) => {
                    const Icon = nodeType.icon;
                    return (
                      <Button
                        key={nodeType.type}
                        variant="outline"
                        className="w-full justify-start h-auto py-3 hover:shadow-md transition-all group"
                        onClick={() => addNode(nodeType.type)}
                      >
                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${nodeType.color} mr-3 group-hover:scale-110 transition-transform`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left flex-1">
                          <p className="text-sm">{nodeType.label}</p>
                          <p className="text-xs text-muted-foreground">{nodeType.description}</p>
                        </div>
                        <Plus className="ml-2 h-4 w-4 opacity-50 group-hover:opacity-100" />
                      </Button>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuración del Flujo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la Automatización</Label>
                <Input
                  id="name"
                  placeholder="Ej: Envío de ofertas diarias"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  placeholder="Describe qué hace esta automatización..."
                  value={workflowDescription}
                  onChange={(e) => setWorkflowDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="trigger">Tipo de Activación</Label>
                <Select value={trigger} onValueChange={setTrigger}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="schedule">Programado (Cron)</SelectItem>
                    <SelectItem value="webhook">Webhook HTTP</SelectItem>
                    <SelectItem value="event">Evento del Sistema</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {trigger === 'schedule' && (
                <div className="space-y-2">
                  <Label htmlFor="cron">Expresión Cron</Label>
                  <Input
                    id="cron"
                    placeholder="0 9 * * 1-5"
                    defaultValue="0 9 * * *"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ejemplo: 0 9 * * * = Todos los días a las 9:00 AM
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}